import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import {
    withScriptjs,
    withGoogleMap,
    GoogleMap,
    Marker,
} from 'react-google-maps';
import InfoBox from 'react-google-maps/lib/components/addons/InfoBox';
import MarkerClusterer from 'react-google-maps/lib/components/addons/MarkerClusterer';
import getGoogleMapApiKey from 'services/getGoogleMapApiKey';
import getDayName from 'services/getDayName';
import isInHourlyRange from 'services/isInHourlyRange';
import { getUserLocation } from 'data/UserLocation/actions';
import { setCurrentLocation } from 'data/CurrentLocation/actions';
import { hideListContent } from 'data/ListContent/actions';
import { setZoom, setCenter } from 'data/MapSettings/actions';
import { getFilteredLocations } from 'services/selectors';
import TradingHoursToday from 'components/TradingHoursToday';
import CustomInfoBox from './components/CustomInfoBox';

import mapStyles from './styles';
import markerIcon from '-!raw-loader!./icon-bowl.svg'; // eslint-disable-line
import markerIconActive from '-!raw-loader!./icon-bowl-active.svg'; // eslint-disable-line
import markerIconOpen from '-!raw-loader!./icon-bowl-open.svg'; // eslint-disable-line
import markerIconClosed from '-!raw-loader!./icon-bowl-closed.svg'; // eslint-disable-line
import acaiLogo from '-!raw-loader!./acai-logo.svg'; // eslint-disable-line
import youAreHereIcon from '-!raw-loader!./icon-you-are-here.svg'; // eslint-disable-line
import './index.scss';

/*
 * Types definition
 */
const propTypes = {
    locations: PropTypes.objectOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            fullAddress: PropTypes.oneOfType([
                PropTypes.shape({
                    lat: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]),
                    lng: PropTypes.oneOfType([
                        PropTypes.string,
                        PropTypes.number,
                    ]),
                }),
                PropTypes.string,
            ]),
            headquarter: PropTypes.bool,
            tradingHours: PropTypes.shape({
                monday: PropTypes.string,
                tuesday: PropTypes.string,
                wednesday: PropTypes.string,
                thursday: PropTypes.string,
                friday: PropTypes.string,
                saturday: PropTypes.string,
                sunday: PropTypes.string,
            }),
        })
    ),
    mapSettings: PropTypes.shape({
        zoom: PropTypes.number,
        headquarters: PropTypes.bool,
        center: PropTypes.shape({
            lat: PropTypes.number,
            lng: PropTypes.number,
        })
    }),
    userLocation: PropTypes.shape({
        lat: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        lng: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
    }),
    currentLocation: PropTypes.string,
    getUserCurrentLocation: PropTypes.func,
    onMarkerClick: PropTypes.func,
    onZoomChange: PropTypes.func,
};

const defaultProps = {
    locations: null,
    mapSettings: {
        zoom: 5,
        headquarters: false,
    },
    userLocation: null,
    currentLocation: null,
    getUserCurrentLocation: null,
    onMarkerClick: null,
    onZoomChange: null,
};

/*
 * Component
 */
class Map extends React.Component {
    constructor(props) {
        super(props);

        this.mapRef = React.createRef();
        this.fitMapBounds = this.fitMapBounds.bind(this);
        this.getMarkerIcon = this.getMarkerIcon.bind(this);
        this.handleOnMarkerClick = this.handleOnMarkerClick.bind(this);
        this.handleZoomChange = this.handleZoomChange.bind(this);
        this.handleMarkerMouseOver = this.handleMarkerMouseOver.bind(this);
        this.handleMarkerMouseOut = this.handleMarkerMouseOut.bind(this);
    }

    state = {
        hoveredMarker: null,
    };

    componentDidMount() {
        const { getUserCurrentLocation } = this.props;

        if (getUserCurrentLocation) {
            getUserCurrentLocation();
        }

        this.fitMapBounds();
    }

    componentDidUpdate(prevProps) {
        const { mapSettings: { headquarters }, locations } = this.props;

        // don't do anything if headquarters are toggled
        if (prevProps.mapSettings.headquarters !== headquarters) {
            return;
        }

        // fit map bounds to visible markers if locations list has changed
        if (JSON.stringify(prevProps.locations) !== JSON.stringify(locations)) {
            this.fitMapBounds();
        }
    }

    getMarkerIcon(markerId, isHeadquarter, tradingHours) {
        const { currentLocation } = this.props;
        const { hoveredMarker } = this.state;

        if (isHeadquarter) {
            return acaiLogo;
        }

        if (tradingHours && hoveredMarker === markerId) {
            if (isInHourlyRange(tradingHours)) {
                return markerIconOpen;
            }

            return markerIconClosed;
        }

        if (currentLocation === markerId) {
            return markerIconActive;
        }

        return markerIcon;
    }

    fitMapBounds() {
        const { locations } = this.props;
        const bounds = new window.google.maps.LatLngBounds();

        Object.keys(locations).forEach((key) => {
            const { fullAddress } = locations[key];

            if (!fullAddress) {
                return;
            }

            bounds.extend(new window.google.maps.LatLng(
                parseFloat(fullAddress.lat),
                parseFloat(fullAddress.lng)
            ));
        });

        this.mapRef.current.fitBounds(bounds);
    }

    handleOnMarkerClick(id) {
        return () => {
            const { onMarkerClick, currentLocation, locations } = this.props;

            if (!onMarkerClick) {
                return;
            }

            // if same marker is clicked again hide its location details
            if (id === currentLocation) {
                onMarkerClick(null);
                return;
            }

            let yOffset = 0.06;

            if (window.matchMedia('(max-width: 767px)').matches) {
                yOffset = 0.03;
            }

            const currentLocationAddress = locations[id].fullAddress;

            if (currentLocationAddress) {
                const latLng = {
                    lat: parseFloat(currentLocationAddress.lat) + yOffset,
                    lng: currentLocationAddress.lng,
                };

                onMarkerClick(id, latLng);

                return;
            }

            onMarkerClick(id);
        };
    }

    handleMarkerMouseOver(id) {
        return () => {
            this.setState({
                hoveredMarker: id,
            });
        };
    }

    handleMarkerMouseOut() {
        this.setState({
            hoveredMarker: null,
        });
    }

    handleZoomChange() {
        const { onZoomChange } = this.props;

        if (onZoomChange) {
            onZoomChange(this.mapRef.current.getZoom());
        }
    }

    render() {
        const {
            mapSettings,
            locations,
            userLocation,
            currentLocation,
        } = this.props;

        return (
            <GoogleMap
                defaultZoom={mapSettings.zoom}
                defaultCenter={mapSettings.center}
                defaultOptions={{
                    styles: mapStyles,
                    streetViewControl: false,
                    scaleControl: false,
                    mapTypeControl: false,
                    panControl: false,
                    zoomControl: false,
                    rotateControl: false,
                    fullscreenControl: false,
                }}
                zoom={mapSettings.zoom}
                center={mapSettings.center}
                ref={this.mapRef}
                onZoomChanged={this.handleZoomChange}
            >
                <MarkerClusterer
                    averageCenter
                    gridSize={60}
                >
                    {locations && Object.keys(locations).map((key) => {
                        const {
                            fullAddress,
                            headquarter,
                            title,
                            tradingHours,
                            shortAddress,
                        } = locations[key];

                        if (!fullAddress) {
                            return null;
                        }

                        const icon = this.getMarkerIcon(
                            key,
                            headquarter,
                            tradingHours[getDayName()]
                        );
                        const name = headquarter ? 'Acai Headquarter' : 'Acai Brothers';

                        return (
                            <Marker
                                key={locations[key].title}
                                position={{
                                    lat: parseFloat(fullAddress.lat),
                                    lng: parseFloat(fullAddress.lng),
                                }}
                                icon={{
                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(icon)}`,
                                    scaledSize: new window.google.maps.Size(54, 64),
                                }}
                                onClick={this.handleOnMarkerClick(key)}
                                onMouseOver={this.handleMarkerMouseOver(key)}
                                onMouseOut={this.handleMarkerMouseOut}
                                onFocus={this.handleMarkerMouseOver(key)}
                                onBlur={this.handleMarkerMouseOut}
                            >
                                {(currentLocation === key) && (
                                    <InfoBox
                                        options={{
                                            alignBottom: true,
                                            pixelOffset: new window.google.maps.Size(-90, -50),
                                        }}
                                    >
                                        <CustomInfoBox>
                                            <h4>{name} - <span>{title}</span></h4>
                                            <p>{shortAddress}</p>
                                            {tradingHours
                                            && (
                                                <TradingHoursToday
                                                    tradingHours={
                                                        locations[key].tradingHours[getDayName()]
                                                    }
                                                />
                                            )}
                                        </CustomInfoBox>
                                    </InfoBox>
                                )}
                            </Marker>
                        );
                    })}
                </MarkerClusterer>
                {userLocation && (
                    <Marker
                        position={{
                            lat: parseFloat(userLocation.lat),
                            lng: parseFloat(userLocation.lng),
                        }}
                        icon={{
                            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(youAreHereIcon)}`,
                            anchor: new window.google.maps.Point(0, -15),
                        }}
                    />
                )}
            </GoogleMap>
        );
    }
}

Map.propTypes = propTypes;
Map.defaultProps = defaultProps;

const mapStateToProps = state => ({
    locations: getFilteredLocations(state),
    mapSettings: state.mapSettings,
    userLocation: state.userLocation,
    currentLocation: state.currentLocation,
});


const mapDispatchToProps = dispatch => ({
    getUserCurrentLocation: () => dispatch(getUserLocation()),
    onMarkerClick: (id, latLng) => {
        dispatch(setCurrentLocation(id));
        dispatch(hideListContent());
        dispatch(setCenter(latLng));
    },
    onZoomChange: zoom => dispatch(setZoom(zoom))
});

/*
 * HOC
 */
const enhance = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withProps({
        googleMapURL: `https://maps.googleapis.com/maps/api/js?key=${getGoogleMapApiKey()}&v=3.exp`,
        loadingElement: <div className="map-loading-element" />,
        containerElement: <div className="map-container-element" />,
        mapElement: <div className="map-element" />,
    }),
    withScriptjs,
    withGoogleMap
);

export default enhance(Map);
